<template>
  <div class="bookings">
    <div class="bookings-container q-pa-md">
      <header class="flex justify-between q-pb-md">
        <h2 class="text-h4 q-my-sm">{{ $t('bookings.myBookings') }}</h2>
        <q-btn
          :icon="headerButtonIcon"
          :color="headerButtonColor"
          :label="headerButtonLabel"
          class="header-button"
          @click="onHeaderButtonClick"
        />
      </header>

      <div v-if="isLoading" class="q-pa-xl text-center">
        <q-spinner color="primary" size="3em" />
        <div class="q-mt-md">{{ $t('bookings.loading') }}</div>
      </div>

      <div v-else-if="allBookings.length === 0" class="q-my-lg">
        <q-banner rounded class="bg-blue-1 text-blue-9">
          {{ $t('bookings.noActiveBookings') }}
        </q-banner>
      </div>

      <div v-else class="bookings-list q-gutter-md">
        <q-select
          v-model="selectedStatusFilter"
          :options="statusOptions"
          class="filter-select-btn"
          emit-value
          map-options
          outlined
          dense
          :label="$t('bookings.filterBy')"
        />

        <BookingsCard
          v-for="booking in filteredBookings"
          :key="booking.id"
          :booking="booking"
          :user-type="userType"
        >
          <template #actions="{ booking }">
            <slot
              name="booking-actions"
              :booking="booking"
              :handlers="actionHandlers"
            ></slot>
          </template>
        </BookingsCard>
      </div>
    </div>
  </div>
</template>

<script>
import BookingsMixin from '@/pages/bookings/mixins/BookingsMixin';
import BookingsCard from '@/pages/bookings/components/BookingsCard.vue';
import { mapGetters, mapActions } from 'vuex';
import { notifySuccess, notifyError } from '@/utils/notify';

export default {
  name: 'BookingsBase',
  components: { BookingsCard },
  mixins: [BookingsMixin],
  props: {
    userType: {
      type: String,
      required: true,
      validator: (value) => ['organizer', 'photographer'].includes(value),
    },
    headerButtonIcon: {
      type: String,
      required: true,
    },
    headerButtonColor: {
      type: String,
      default: 'primary',
    },
    headerButtonLabel: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedStatusFilter: null,
      processing: { id: null, status: null },
    };
  },
  computed: {
    ...mapGetters('bookings', ['allBookings', 'isLoading']),
    filteredBookings() {
      return this.filterBookingsByStatus(
        this.allBookings,
        this.selectedStatusFilter
      );
    },
    statusOptions() {
      return this.getStatusOptions();
    },
    actionHandlers() {
      return {
        updateStatus: this.handleStatusUpdate,
        isProcessing: this.isProcessing,
      };
    },
  },
  methods: {
    ...mapActions('bookings', ['fetchAllBookings', 'updateBookingStatus']),
    onHeaderButtonClick() {
      this.$emit('header-button-click');
    },
    isProcessing(bookingId, status) {
      return (
        this.processing.id === bookingId && this.processing.status === status
      );
    },
    openDialog(booking, action, onConfirm) {
      const dialogConfig = {
        title: this.$t(`bookings.dialog.${action}.title`),
        message: this.$t(`bookings.dialog.${action}.message`, {
          name: this.getBookingDisplayName(booking),
          date: this.formatDate(booking.date),
        }),
        html: true,
        persistent: true,
        ok: {
          label: this.$t(`bookings.dialog.${action}.buttonYes`),
          color: this.getDialogColor(action),
        },
        cancel: {
          label: this.$t(`bookings.dialog.${action}.buttonNo`),
          color: 'grey',
        },
      };

      this.$q.dialog(dialogConfig).onOk(onConfirm);
    },
    getBookingDisplayName(booking) {
      const user =
        this.userType === 'photographer'
          ? booking.organizer
          : booking.photographer;
      return this.displayNameOrUsernameFallback(user);
    },
    getDialogColor(action) {
      const colorMap = {
        accept: 'positive',
        complete: 'positive',
        cancel: 'negative',
        reject: 'negative',
      };
      return colorMap[action] || 'primary';
    },
    async handleStatusUpdate(bookingId, newStatus, successKey, errorKey) {
      this.processing = { id: bookingId, status: newStatus };

      try {
        await this.updateBookingStatus({ bookingId, newStatus });
        notifySuccess(successKey);
      } catch {
        notifyError(errorKey);
      } finally {
        this.processing = { id: null, status: null };
      }
    },
  },
  watch: {
    selectedStatusFilter(newVal) {
      this.saveStatusFilter(newVal);
    },
  },
  mounted() {
    this.selectedStatusFilter = this.loadStatusFilter();
    this.fetchAllBookings().catch(() => {
      notifyError('bookings.loadError');
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/bookings.scss';
</style>
